<template>
  <div>
    <LoadingSpinner v-if="!order" />
    <template v-else>
      <Form @submit="saveOrder">
        <FormSection>
          <OrderTypeSelect v-model="order.type" :company="order.company" :disabled="order.status !== 'open'" />
        </FormSection>

        <EmptyingForm v-if="order.type == 'EmptyingOrder'" v-model="order" />
        <GatheringForm v-if="order.type == 'GatheringOrder'" v-model="order" />
        <GatheringPickupForm v-if="order.type == 'GatheringPickupOrder'" v-model="order" />
        <FreeSwitchingForm v-if="order.type == 'FreeSwitchingOrder'" v-model="order" />
        <OverheadLoadingForm v-if="order.type == 'OverheadLoadingOrder'" v-model="order" />
        <PickupForm v-if="order.type == 'PickupOrder'" v-model="order" />
        <PickupAndWeighForm v-if="order.type == 'PickupAndWeighOrder'" v-model="order" />
        <PositioningForm v-if="order.type == 'PositioningOrder'" v-model="order" />
        <SwitchingForm v-if="order.type == 'SwitchingOrder'" v-model="order" />

        <FollowUpOrderTypeForm v-model="order.follow_up_order_type" :order="order" />

        <BillingSection :order="order" class="orderDetail__billing-section" />

        <Button
          type="button"
          @click="resetToOpen"
          :disabled="!order.resettable_to_open"
          v-if="order.status == 'assigned'"
        >
          {{ $t('orderDetail.resetToOpen') }}
        </Button>
        <Button type="button" @click="transitionStatus" :disabled="orderChanged" v-if="nextStatus">
          <span
            v-if="
              nextStatus === 'closed' &&
              order.company.processableByRUMS &&
              [ORDER_TYPE.OVERHEAD_LOADING, ORDER_TYPE.EMPTYING, ORDER_TYPE.POSITIONING].includes(order.type)
            "
            >{{ $t('orderDetail.sendToRUMS') }}
          </span>
          <span v-else>{{
            $t('orderDetail.transitionStatus', {
              status: $t(`orders.status${capitalize(nextStatus)}`),
            })
          }}</span>
        </Button>
        <Button type="submit" :disabled="!orderChanged">{{ $t('orderDetail.updateOrder') }}</Button>
        <Button type="button" :disabled="order.status != 'open'" color="red" @click="cancelOrder">{{
          $t('orderDetail.cancel')
        }}</Button>
      </Form>
      <ActionHistory :order="order" />
    </template>
  </div>
</template>

<script>
import Button from '@/components/Button/index.vue'
import FormSection from '@/components/Form/Section/index.vue'
import Form from '@/components/Form/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import ActionHistory from '@/components/Orders/ActionHistory/index.vue'
import EmptyingForm from '@/components/Orders/forms/EmptyingForm.vue'
import FreeSwitchingForm from '@/components/Orders/forms/FreeSwitchingForm.vue'
import GatheringForm from '@/components/Orders/forms/GatheringForm.vue'
import GatheringPickupForm from '@/components/Orders/forms/GatheringPickupForm.vue'
import OverheadLoadingForm from '@/components/Orders/forms/OverheadLoadingForm.vue'
import PickupAndWeighForm from '@/components/Orders/forms/PickupAndWeighForm.vue'
import PickupForm from '@/components/Orders/forms/PickupForm.vue'
import PositioningForm from '@/components/Orders/forms/PositioningForm.vue'
import SwitchingForm from '@/components/Orders/forms/SwitchingForm.vue'
import BillingSection from '@/components/Orders/forms/sections/BillingSection'
import FollowUpOrderTypeForm from '@/components/Orders/forms/sections/elements/FollowUpOrderTypeForm.vue'
import OrderTypeSelect from '@/components/Orders/forms/sections/elements/OrderTypeSelect.vue'
import stringUtils from '@/components/mixins/stringUtils'

import ordersApi, { ORDER_TYPE } from '@/api/orders'

export default {
  name: 'OrderDetail',
  mixins: [stringUtils],
  components: {
    ActionHistory,
    BillingSection,
    Button,
    EmptyingForm,
    FreeSwitchingForm,
    FollowUpOrderTypeForm,
    Form,
    FormSection,
    GatheringForm,
    GatheringPickupForm,
    LoadingSpinner,
    OrderTypeSelect,
    OverheadLoadingForm,
    PickupForm,
    PickupAndWeighForm,
    PositioningForm,
    SwitchingForm,
  },
  inject: ['showPopup'],
  data: () => ({
    order: null,
    ORDER_TYPE,
    orderChangedCount: -2, // we get two initial updates from the v-model
  }),
  computed: {
    nextStatus() {
      const statuses = ['open', 'assigned', 'done', 'closed']

      return statuses[statuses.indexOf(this.order.status) + 1]
    },
    orderChanged() {
      return this.orderChangedCount > 0
    },
  },
  methods: {
    saveOrder() {
      this.updateOrder(this.order)
    },
    async cancelOrder() {
      if (!(await this.showPopup({ content: this.$t('orders.deleteConfirmation'), type: 'confirm' }))) return

      await ordersApi.delete(this.order.uuid)

      await this.closeModal()
    },
    transitionStatus() {
      this.updateOrder({ ...this.order, status: this.nextStatus })
    },
    resetToOpen() {
      this.updateOrder({ status: 'open' })
    },
    closeModal() {
      this.$router.push({
        path: '/orders',
        query: this.$router.currentRoute.query,
      })
    },
    async updateOrder(params) {
      const { success, data } = await ordersApi.update(this.order.uuid, params)

      if (!success) return

      this.order = data.order

      this.orderChangedCount = -2
    },
  },
  async mounted() {
    this.order = await ordersApi.get(this.$route.params.orderUuid)
  },
  watch: {
    order: {
      handler() {
        this.orderChangedCount = this.orderChangedCount + 1
      },
      deep: true,
    },
  },
}
</script>

<style scoped lang="scss">
.orderDetail__header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
}
.orderDetail__headline {
  margin: 0;
}
.orderDetail__close-button {
  width: 3rem;
  font-size: 2rem;
  background: none;
  border: none;
  cursor: pointer;
}

.orderDetail__billing-section {
  margin-bottom: 2rem;
}
</style>
